import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  ArrowLeft, Building2, Mail, Phone, MapPin, FileText, 
  Plus, Pencil, Trash2, Check, X, Calendar, TrendingUp, TrendingDown,
  Receipt, CreditCard, Clock, Save
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { motion } from "framer-motion";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

const formatDate = (date) => {
  if (!date) return "-";
  return new Date(date).toLocaleDateString('tr-TR');
};

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [transactionDialog, setTransactionDialog] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  
  const [customerForm, setCustomerForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    address: "",
    notes: "",
    total_receivable: 0
  });

  const [transactionForm, setTransactionForm] = useState({
    type: "receivable",
    category: "fatura",
    amount: "",
    description: "",
    due_date: new Date().toISOString().split('T')[0],
    paid: false
  });

  const fetchCustomer = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers/${id}`, { withCredentials: true });
      setCustomer(response.data);
      setCustomerForm({
        name: response.data.name || "",
        email: response.data.email || "",
        phone: response.data.phone || "",
        company: response.data.company || "",
        address: response.data.address || "",
        notes: response.data.notes || "",
        total_receivable: response.data.total_receivable || 0
      });
    } catch (error) {
      toast.error("Müşteri bilgileri yüklenemedi");
      navigate("/customers");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchTransactions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/customers/${id}/transactions`, { withCredentials: true });
      setTransactions(response.data);
    } catch (error) {
      // Silent error handling
    }
  }, [id]);

  useEffect(() => {
    fetchCustomer();
    fetchTransactions();
  }, [fetchCustomer, fetchTransactions]);

  const handleSaveCustomer = async () => {
    try {
      await axios.put(`${API}/customers/${id}`, customerForm, { withCredentials: true });
      toast.success("Müşteri bilgileri güncellendi");
      setEditMode(false);
      fetchCustomer();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    }
  };

  const handleSaveTransaction = async () => {
    try {
      const data = {
        ...transactionForm,
        amount: parseFloat(transactionForm.amount),
        customer_id: id,
        customer_name: customer.name
      };

      if (editingTransaction) {
        await axios.put(`${API}/payments/${editingTransaction.id}`, data, { withCredentials: true });
        toast.success("İşlem güncellendi");
      } else {
        await axios.post(`${API}/payments`, data, { withCredentials: true });
        toast.success("İşlem eklendi");
      }
      
      setTransactionDialog(false);
      setEditingTransaction(null);
      resetTransactionForm();
      fetchTransactions();
      fetchCustomer(); // Bakiye güncellensin
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm("Bu işlemi silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/payments/${transactionId}`, { withCredentials: true });
      toast.success("İşlem silindi");
      fetchTransactions();
      fetchCustomer();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const handleMarkPaid = async (transaction) => {
    try {
      await axios.put(`${API}/payments/${transaction.id}`, {
        ...transaction,
        paid: !transaction.paid,
        paid_date: !transaction.paid ? new Date().toISOString().split('T')[0] : null
      }, { withCredentials: true });
      toast.success(transaction.paid ? "Ödenmedi olarak işaretlendi" : "Ödendi olarak işaretlendi");
      fetchTransactions();
      fetchCustomer();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const openEditTransaction = (transaction) => {
    setEditingTransaction(transaction);
    setTransactionForm({
      type: transaction.type || "receivable",
      category: transaction.category || "fatura",
      amount: transaction.amount?.toString() || "",
      description: transaction.description || "",
      due_date: transaction.due_date || new Date().toISOString().split('T')[0],
      paid: transaction.paid || false
    });
    setTransactionDialog(true);
  };

  const resetTransactionForm = () => {
    setTransactionForm({
      type: "receivable",
      category: "fatura",
      amount: "",
      description: "",
      due_date: new Date().toISOString().split('T')[0],
      paid: false
    });
  };

  // İşlemleri kategoriye göre ayır
  const invoices = transactions.filter(t => t.category === "fatura" || (!t.paid && t.type === "receivable"));
  const payments = transactions.filter(t => t.category === "tahsilat" || (t.paid && t.type === "receivable"));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-12">
        <p className="text-zinc-500">Müşteri bulunamadı</p>
        <Button onClick={() => navigate("/customers")} className="mt-4">
          Müşterilere Dön
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/customers")}
            className="hover:bg-zinc-800"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-outfit font-semibold text-zinc-50">{customer.name}</h1>
            <p className="text-zinc-500 text-sm mt-1">Müşteri Detayları</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {editMode ? (
            <>
              <Button 
                variant="outline" 
                onClick={() => setEditMode(false)}
                className="border-zinc-700"
              >
                <X className="w-4 h-4 mr-2" />
                İptal
              </Button>
              <Button 
                onClick={handleSaveCustomer}
                className="bg-emerald-500 hover:bg-emerald-400 text-white"
              >
                <Save className="w-4 h-4 mr-2" />
                Kaydet
              </Button>
            </>
          ) : (
            <Button 
              onClick={() => setEditMode(true)}
              className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
            >
              <Pencil className="w-4 h-4 mr-2" />
              Düzenle
            </Button>
          )}
        </div>
      </div>

      {/* Customer Info Card */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-sm p-6">
          <h2 className="text-lg font-medium text-zinc-50 mb-4 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-amber-400" />
            Müşteri Bilgileri
          </h2>
          
          {editMode ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Müşteri Adı</Label>
                <Input 
                  value={customerForm.name}
                  onChange={(e) => setCustomerForm({...customerForm, name: e.target.value})}
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Şirket</Label>
                <Input 
                  value={customerForm.company}
                  onChange={(e) => setCustomerForm({...customerForm, company: e.target.value})}
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-zinc-400">E-posta</Label>
                <Input 
                  type="email"
                  value={customerForm.email}
                  onChange={(e) => setCustomerForm({...customerForm, email: e.target.value})}
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Telefon</Label>
                <Input 
                  value={customerForm.phone}
                  onChange={(e) => setCustomerForm({...customerForm, phone: e.target.value})}
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-zinc-400">Adres</Label>
                <Input 
                  value={customerForm.address}
                  onChange={(e) => setCustomerForm({...customerForm, address: e.target.value})}
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-zinc-400">Notlar</Label>
                <Textarea 
                  value={customerForm.notes}
                  onChange={(e) => setCustomerForm({...customerForm, notes: e.target.value})}
                  className="mt-1 bg-zinc-950 border-zinc-800"
                  rows={3}
                />
              </div>
              <div>
                <Label className="text-zinc-400">Toplam Alacak (Manuel)</Label>
                <Input 
                  type="number"
                  value={customerForm.total_receivable}
                  onChange={(e) => setCustomerForm({...customerForm, total_receivable: parseFloat(e.target.value) || 0})}
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                {customer.company && (
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Building2 className="w-4 h-4" />
                    <span>{customer.company}</span>
                  </div>
                )}
                {customer.email && (
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Mail className="w-4 h-4" />
                    <span>{customer.email}</span>
                  </div>
                )}
                {customer.phone && (
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Phone className="w-4 h-4" />
                    <span>{customer.phone}</span>
                  </div>
                )}
                {customer.address && (
                  <div className="flex items-center gap-2 text-zinc-400">
                    <MapPin className="w-4 h-4" />
                    <span>{customer.address}</span>
                  </div>
                )}
              </div>
              {customer.notes && (
                <div className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm">
                  <p className="text-xs text-zinc-500 mb-1">Notlar</p>
                  <p className="text-sm text-zinc-400">{customer.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Balance Card */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-6">
          <h2 className="text-lg font-medium text-zinc-50 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-emerald-400" />
            Bakiye Durumu
          </h2>
          <div className="space-y-4">
            <div className="p-4 bg-emerald-400/10 border border-emerald-400/20 rounded-sm">
              <p className="text-xs text-emerald-400 mb-1">Toplam Alacak</p>
              <p className="text-2xl font-mono font-bold text-emerald-400">
                {formatCurrency(customer.total_receivable)}
              </p>
            </div>
            <div className="p-4 bg-rose-400/10 border border-rose-400/20 rounded-sm">
              <p className="text-xs text-rose-400 mb-1">Toplam Borç</p>
              <p className="text-2xl font-mono font-bold text-rose-400">
                {formatCurrency(customer.total_payable)}
              </p>
            </div>
            <div className="p-4 bg-zinc-800 rounded-sm">
              <p className="text-xs text-zinc-500 mb-1">Net Bakiye</p>
              <p className={`text-2xl font-mono font-bold ${(customer.total_receivable - customer.total_payable) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                {formatCurrency((customer.total_receivable || 0) - (customer.total_payable || 0))}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Section */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm">
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <h2 className="text-lg font-medium text-zinc-50 flex items-center gap-2">
            <Receipt className="w-5 h-5 text-amber-400" />
            Fatura & Tahsilat Geçmişi
          </h2>
          <Button 
            onClick={() => { resetTransactionForm(); setTransactionDialog(true); }}
            size="sm"
            className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
          >
            <Plus className="w-4 h-4 mr-2" />
            Yeni İşlem
          </Button>
        </div>

        {transactions.length === 0 ? (
          <div className="p-8 text-center">
            <FileText className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-500">Henüz işlem kaydı yok</p>
            <Button 
              onClick={() => { resetTransactionForm(); setTransactionDialog(true); }}
              variant="outline"
              className="mt-4 border-zinc-700"
            >
              İlk işlemi ekleyin
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-800">
                  <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Tarih</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Tür</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Açıklama</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Tutar</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-zinc-500 uppercase">Durum</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((t, index) => (
                  <motion.tr 
                    key={t.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.03 }}
                    className="border-b border-zinc-800 hover:bg-zinc-800/50"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-zinc-500" />
                        <span className="text-sm text-zinc-400">{formatDate(t.due_date)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-1 rounded ${
                        t.category === 'fatura' 
                          ? 'bg-amber-400/10 text-amber-400' 
                          : 'bg-emerald-400/10 text-emerald-400'
                      }`}>
                        {t.category === 'fatura' ? 'Fatura' : 'Tahsilat'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-zinc-300 max-w-xs truncate">{t.description || "-"}</p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-sm font-mono ${
                        t.category === 'tahsilat' ? 'text-emerald-400' : 'text-amber-400'
                      }`}>
                        {t.category === 'tahsilat' ? '-' : '+'}{formatCurrency(t.amount)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button 
                        onClick={() => handleMarkPaid(t)}
                        className={`text-xs px-2 py-1 rounded cursor-pointer transition-colors ${
                          t.paid 
                            ? 'bg-emerald-400/10 text-emerald-400 hover:bg-emerald-400/20' 
                            : 'bg-rose-400/10 text-rose-400 hover:bg-rose-400/20'
                        }`}
                      >
                        {t.paid ? 'Ödendi' : 'Bekliyor'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => openEditTransaction(t)}
                          className="h-8 w-8 hover:bg-zinc-800"
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteTransaction(t.id)}
                          className="h-8 w-8 hover:bg-rose-400/10 text-rose-400"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Transaction Dialog */}
      <Dialog open={transactionDialog} onOpenChange={setTransactionDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">
              {editingTransaction ? 'İşlem Düzenle' : 'Yeni İşlem Ekle'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Tür</Label>
                <select 
                  value={transactionForm.category}
                  onChange={(e) => setTransactionForm({...transactionForm, category: e.target.value})}
                  className="w-full mt-1 px-3 py-2 bg-zinc-950 border border-zinc-800 rounded-sm text-zinc-50"
                >
                  <option value="fatura">Fatura</option>
                  <option value="tahsilat">Tahsilat</option>
                </select>
              </div>
              <div>
                <Label className="text-zinc-400">Tarih</Label>
                <Input 
                  type="date"
                  value={transactionForm.due_date}
                  onChange={(e) => setTransactionForm({...transactionForm, due_date: e.target.value})}
                  className="mt-1 bg-zinc-950 border-zinc-800"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400">Tutar (TL)</Label>
              <Input 
                type="number"
                step="0.01"
                value={transactionForm.amount}
                onChange={(e) => setTransactionForm({...transactionForm, amount: e.target.value})}
                className="mt-1 bg-zinc-950 border-zinc-800"
                placeholder="0.00"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Açıklama</Label>
              <Textarea 
                value={transactionForm.description}
                onChange={(e) => setTransactionForm({...transactionForm, description: e.target.value})}
                className="mt-1 bg-zinc-950 border-zinc-800"
                placeholder="Fatura açıklaması..."
                rows={2}
              />
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                id="paid"
                checked={transactionForm.paid}
                onChange={(e) => setTransactionForm({...transactionForm, paid: e.target.checked})}
                className="rounded border-zinc-700"
              />
              <Label htmlFor="paid" className="text-zinc-400 cursor-pointer">Ödendi olarak işaretle</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransactionDialog(false)} className="border-zinc-700">
              İptal
            </Button>
            <Button onClick={handleSaveTransaction} className="bg-amber-400 hover:bg-amber-300 text-zinc-950">
              {editingTransaction ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomerDetail;
